import React, { useEffect, useState } from 'react';
import '../utils/windowHandlers'; // Import window handlers (defines onFCMTokenReceived, etc.)

/**
 * AndroidTokenManager Component
 * 
 * This component manages FCM (Firebase Cloud Messaging) token for Android WebView apps.
 * It works in conjunction with windowHandlers.js which defines the actual handlers.
 * 
 * Flow:
 * 1. On mount, check if running in Android WebView
 * 2. If Android, request FCM token from native side
 * 3. When token is received, windowHandlers.js will store it and register with backend
 * 4. On login, windowHandlers.js registers the stored token with the user
 * 5. On logout, windowHandlers.js deactivates the token
 */
const AndroidTokenManager = () => {
    const [isAndroidApp, setIsAndroidApp] = useState(false);

    useEffect(() => {
        // Check if running in Android WebView
        const checkAndroidApp = () => {
            const isAndroid = typeof window.Android !== 'undefined';
            console.log('[AndroidTokenManager] Is Android WebView:', isAndroid);
            return isAndroid;
        };
        
        const androidDetected = checkAndroidApp();
        setIsAndroidApp(androidDetected);

        if (androidDetected) {
            initializeAndroidIntegration();
        }

        // Listen for user login event to trigger token registration
        const handleUserLoggedIn = () => {
            console.log('[AndroidTokenManager] User logged in event detected');
            // If we have a stored FCM token, the windowHandlers.js will handle registration
            // This event is dispatched from Login.js after successful login
            if (window.Android && window.Android.requestFCMToken) {
                // Request fresh token after login to ensure it's registered
                console.log('[AndroidTokenManager] Requesting fresh FCM token after login');
                window.Android.requestFCMToken();
            }
        };

        // Listen for user logout event
        const handleUserLoggedOut = () => {
            console.log('[AndroidTokenManager] User logged out event detected');
            // Token deactivation is handled by windowHandlers.js handleLogout
        };

        window.addEventListener('userLoggedIn', handleUserLoggedIn);
        window.addEventListener('userLoggedOut', handleUserLoggedOut);

        // Cleanup function
        return () => {
            window.removeEventListener('userLoggedIn', handleUserLoggedIn);
            window.removeEventListener('userLoggedOut', handleUserLoggedOut);
        };
    }, []);

    const initializeAndroidIntegration = () => {
        // Request FCM token when app loads
        if (window.Android && window.Android.requestFCMToken) {
            console.log('[AndroidTokenManager] Requesting FCM token from Android...');
            // Small delay to ensure Android WebView is fully ready
            setTimeout(() => {
                try {
                    window.Android.requestFCMToken();
                    console.log('[AndroidTokenManager] FCM token request sent');
                } catch (error) {
                    console.error('[AndroidTokenManager] Error requesting FCM token:', error);
                }
            }, 500);
        } else {
            console.warn('[AndroidTokenManager] Android interface not available for FCM token request');
        }
    };

    return (
        <div style={{ display: 'none' }}>
            {/* This component is invisible but handles Android FCM token management */}
            {isAndroidApp && <span data-testid="android-token-manager">Active</span>}
        </div>
    );
};

export default AndroidTokenManager; 